import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ListChecks, Clock, Trophy, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import {
  getClientFingerprint,
  isAlreadyParticipatedError,
  alreadyParticipatedMessage,
  extractErrorMessage,
} from "@/lib/fingerprint";
import { alpha } from "@/lib/color";
import { accentColor, accentBackground } from "@/lib/gameStyle";
import QuizQuestion from "@/components/QuizQuestion";
import QuizResult from "@/components/QuizResult";

/**
 * QuizGame — public player UI for `quiz` games.
 * intro → one question at a time → server grading → result (retry once).
 */
export default function QuizGame({ game, participant, ctasCompleted, receipt, refId, onAlreadyParticipated, onResult, onSubmitted }) {
  const { t } = useTranslation();
  const questions = game.quiz_questions || [];
  const theme = accentColor(game);
  const themeBg = accentBackground(game);
  const btnText = game.button_text_color || "#fff";
  const timePerQuestion = Number(game.quiz_time_per_question) || 0;
  const [phase, setPhase] = useState("intro"); // intro | question | submitting | result
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => questions.map(() => []));
  const [sessionId, setSessionId] = useState(null);
  const [result, setResult] = useState(null);

  async function submit(finalAnswers) {
    setPhase("submitting");
    try {
      const { data } = await axios.post(`${API}/public/games/${game.slug}/quiz-submit`, {
        participant,
        ctas_completed: ctasCompleted || [],
        fingerprint: getClientFingerprint(),
        receipt_url: receipt?.url || null,
        receipt_ocr: receipt?.ocr || null,
        answers: finalAnswers,
        session_id: sessionId,
        ref: refId || null,
      });
      setSessionId(data.session_id);
      setResult(data);
      setPhase("result");
      if (data.all_correct || !data.can_retry) onSubmitted?.();
      if (!data.all_correct && !data.can_retry) {
        // Last attempt failed → "Dommage…" pop-up on top of the detailed screen.
        setTimeout(
          () =>
            onResult?.({
              is_win: false,
              result_label: t("play.quiz_score", { score: data.score, total: data.total }),
              message: game.lose_message || t("play.quiz_lose_default"),
            }),
          700
        );
      }
    } catch (e) {
      if (isAlreadyParticipatedError(e)) {
        onAlreadyParticipated?.(alreadyParticipatedMessage(e));
      } else {
        toast.error(extractErrorMessage(e, "Error"));
        setPhase("question");
      }
    }
  }

  const next = useCallback(() => {
    if (idx < questions.length - 1) {
      setIdx(idx + 1);
    } else {
      submit(answers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, answers, questions.length]);

  function retry() {
    setAnswers(questions.map(() => []));
    setIdx(0);
    setResult(null);
    setPhase("question");
  }

  if (!questions.length) {
    return (
      <div className="qrise-card p-8 text-center text-sm text-muted-foreground" data-testid="quiz-empty">
        {t("play.quiz_empty")}
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto" data-testid="quiz-game">
      <AnimatePresence mode="wait">
        {phase === "intro" && (
          <motion.div
            key="intro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="qrise-card p-7 text-center space-y-5"
            data-testid="quiz-intro"
          >
            <div
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
              style={{ background: alpha(theme, 0.12), color: theme }}
            >
              <ListChecks className="h-3 w-3" />
              {t("play.quiz_badge")}
            </div>
            <h3 className="text-2xl font-bold tracking-tight">
              {t("play.quiz_intro_title", { count: questions.length })}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto whitespace-pre-line" data-testid="quiz-intro-body">
              {game.quiz_intro_text?.trim() ||
                t(game.quiz_unlimited_plays ? "play.quiz_intro_body_unlimited" : "play.quiz_intro_body")}
            </p>
            <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5" data-testid="quiz-intro-winners">
                <Trophy className="h-3.5 w-3.5 text-amber-500" />
                {game.quiz_winners_label?.trim() || t("play.quiz_intro_winners", { count: game.winners_count || 1 })}
              </span>
              {timePerQuestion > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5" data-testid="quiz-intro-timer">
                  <Clock className="h-3.5 w-3.5" />
                  {t("play.quiz_intro_timer", { seconds: timePerQuestion })}
                </span>
              )}
            </div>
            <Button
              type="button"
              onClick={() => setPhase("question")}
              className="w-full h-12 rounded-xl text-sm font-bold uppercase tracking-wider hover:opacity-90"
              style={{ background: themeBg, color: btnText }}
              data-testid="quiz-start-btn"
            >
              <Play className="h-4 w-4 mr-1.5" style={{ fill: "currentColor" }} />
              {t("play.quiz_start")}
            </Button>
          </motion.div>
        )}

        {phase === "question" && (
          <QuizQuestion
            key={`q-${idx}-${result?.attempts || 0}`}
            question={questions[idx]}
            index={idx}
            total={questions.length}
            selected={answers[idx]}
            onChange={(sel) => setAnswers((a) => a.map((x, i) => (i === idx ? sel : x)))}
            onNext={next}
            timePerQuestion={timePerQuestion}
            themeColor={theme}
            selectColor={game.quiz_select_color || theme}
            themeBg={themeBg}
            btnText={btnText}
          />
        )}

        {phase === "submitting" && (
          <motion.div
            key="submitting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="qrise-card p-10 flex flex-col items-center gap-3 text-sm text-muted-foreground"
            data-testid="quiz-submitting"
          >
            <Loader2 className="h-7 w-7 animate-spin" style={{ color: theme }} />
            {t("play.quiz_checking")}
          </motion.div>
        )}

        {phase === "result" && result && (
          <QuizResult key="result" game={game} result={result} questions={questions} onRetry={retry} />
        )}
      </AnimatePresence>
    </div>
  );
}
